import { lazy, Suspense, useState } from "react";
import { Select, SearchInput } from "@/components/inputs";
import { PageHead } from "@/components/PageHead";
import { useQuery } from "@tanstack/react-query";

import { assetData } from "@/api/client";
import { ContextStrip } from "@/modules/asset_data/ContextStrip";
import { DevicePicker } from "@/modules/asset_data/picker";
import { RecordTable } from "@/modules/asset_data/RecordTable";

// lazy: Leaflet (~145KB) loads only when a History replay actually renders —
// every other page keeps the pre-map bundle size
const RouteReplay = lazy(() => import("@/modules/asset_data/RouteReplay").then((m) => ({ default: m.RouteReplay })));

/** value for <input type="datetime-local"> from a Date (local, minute precision). */
function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function AssetDataHistoryPage() {
  const [imei, setImei] = useState("");
  const now = new Date();
  const [from, setFrom] = useState(localInput(new Date(now.getTime() - 3600_000)));
  const [to, setTo] = useState(localInput(now));
  // server-side downsampling: raw for short replays, bucketed for long windows
  // (the browser gets one averaged row per bucket, never the raw firehose)
  const [bucket, setBucket] = useState(0);
  const [range, setRange] = useState<{ imei: string; from: string; to: string; bucket: number } | null>(null);

  const q = useQuery({
    queryKey: ["live", "history", range?.imei, range?.from, range?.to, range?.bucket],
    queryFn: () => assetData.history(range!.imei, {
      from: new Date(range!.from).toISOString(),
      to: new Date(range!.to).toISOString(),
      bucket: range!.bucket ? String(range!.bucket) : undefined,
      limit: "5000",
    }),
    enabled: !!range,
  });
  const records = q.data?.records ?? [];
  const signals = q.data?.signals ?? [];

  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · History" title="Replay history"
        desc={<>Step back through a device's decoded records over any window (from the tel/his telemetry history) — same columns as the live table.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <DevicePicker imei={imei} onPick={setImei} />
        {imei && <div style={{ marginTop: 8 }}><ContextStrip imei={imei} /></div>}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
          <div>
            <label className="simlab">From</label>
            <SearchInput type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="simlab">To</label>
            <SearchInput type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <label className="simlab">Resolution</label>
            <Select style={{ minWidth: 130 }} value={bucket}
              onChange={(e) => setBucket(Number(e.target.value))}>
              <option value={0}>Raw records</option>
              <option value={60}>1 min avg</option>
              <option value={600}>10 min avg</option>
              <option value={3600}>1 hour avg</option>
            </Select>
          </div>
          <button className="btn teal sm" style={{ marginBottom: 1 }} disabled={!imei}
            onClick={() => setRange({ imei, from, to, bucket })}
            title={!imei ? "pick a device first" : ""}>Load</button>
          {range && (
            <span style={{ fontSize: 11.5, color: "var(--t-mute)", paddingBottom: 6 }}>
              {q.isFetching ? "loading…" : `${records.length} record${records.length === 1 ? "" : "s"}`}
            </span>
          )}
        </div>
      </div>

      {range ? (
        <>
          {q.data?.truncated && (
            <div className="panel panel-pad" style={{ marginBottom: 14, borderLeft: "3px solid var(--warn)", color: "var(--t-mute)", fontSize: 12.5 }}>
              ⚠ This window holds more than 5,000 {range.bucket ? "buckets" : "records"} — showing the first 5,000.
              Narrow the time window{range.bucket ? "" : " or pick a coarser resolution"} to see everything.
            </div>
          )}
          <Suspense fallback={<div className="panel panel-pad" style={{ height: 120, color: "var(--t-dim)", fontSize: 12, marginBottom: 14 }}>loading map…</div>}>
            <RouteReplay records={records} />
          </Suspense>
          {/* newest-first for reading; the replay above keeps its own
              oldest→newest order for the scrubber */}
          <RecordTable records={records} signals={signals} maxHeight={520} newestFirst />
        </>
      ) : (
        <div className="panel panel-pad cap">
          Pick a device and a time window, then press Load.
        </div>
      )}
    </div>
  );
}
